/**
 * OrbitalCenter
 *
 * 궤도 중앙 로딩 메시지 컴포넌트
 * - 컨테이너 없이 텍스트만 정중앙 배치
 * - 펄스 애니메이션
 * - 아바타 단계 플레이스홀더
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * OrbitalCenter
 *
 * @param {string} stage - 현재 로딩 단계 ID ('threads' | 'friends' | 'friends_avatar')
 * @param {string} message - 표시할 메시지
 * @param {string} subMessage - 부가 메시지 (스레드 수, 친구 수 등)
 * @param {object} size - 중앙 영역 크기 { width, height } (미사용, 호환성 유지)
 * @param {boolean} showAvatarPlaceholders - 아바타 플레이스홀더 표시 여부
 */
const OrbitalCenter = ({
  stage,
  message,
  subMessage,
  size = { width: 200, height: 80 },
  showAvatarPlaceholders = false,
}) => {
  const isAvatarStage = stage === 'friends_avatar';

  return (
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 10,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      <AnimatePresence mode="wait">
        {/* 아바타 플레이스홀더 (아바타 단계에서만) */}
        {isAvatarStage && showAvatarPlaceholders && (
          <motion.div
            key="avatar-placeholders"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 0.75,
                mb: 1.5,
              }}
            >
              {[0, 1, 2, 3, 4].map((idx) => (
                <motion.div
                  key={idx}
                  animate={{
                    scale: [1, 1.15, 1],
                    opacity: [0.4, 1, 0.4],
                  }}
                  transition={{
                    duration: 1.2,
                    delay: idx * 0.12,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 24, md: 28 },
                      height: { xs: 24, md: 28 },
                      borderRadius: '50%',
                      backgroundColor: 'grey.300',
                    }}
                  />
                </motion.div>
              ))}
            </Box>
          </motion.div>
        )}

        {/* 메인 메시지 - 컨테이너 없이 텍스트만 */}
        <motion.div
          key={`message-${stage}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Typography
              sx={{
                fontFamily: '"source-han-serif-korean", "Noto Serif KR", serif',
                fontWeight: 600,
                color: 'text.primary',
                fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                whiteSpace: 'nowrap',
                letterSpacing: '0.02em',
              }}
            >
              {message}
            </Typography>
          </motion.div>
        </motion.div>

        {/* 부가 메시지 */}
        {subMessage && (
          <motion.div
            key={`sub-${stage}`}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <Typography
              variant="caption"
              sx={{
                mt: 0.75,
                color: 'text.secondary',
                fontWeight: 400,
                fontSize: { xs: '0.75rem', md: '0.8rem' },
                opacity: 0.7,
              }}
            >
              {subMessage}
            </Typography>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default OrbitalCenter;
